import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { isEmpty } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Logger } from '@app/utils/logger';
import { environment } from '../../../environments/environment';
import { decrypt } from './graphql.crypto';

/**
 * GraphQL Request Interceptor.
 *
 * @exports
 * @class GraphQLRequestInterceptor
 */
@Injectable()
export class GraphQLRequestInterceptor implements HttpInterceptor {
  private readonly route = inject(ActivatedRoute);
  private readonly storage = inject(LocalStorageService);

  private readonly logger = Logger.getInstance('GraphQLRequestInterceptor');

  /**
   * @description Get token.
   * @readonly
   * @type {string|null}
   * @memberof GraphQLRequestInterceptor
   */
  get token(): string | null {
    const params = new URLSearchParams(window.location.search);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing, prettier/prettier
    const qs = params.get('auth') || params.get('token') || this.route.snapshot.queryParamMap.get('auth') || this.route.snapshot.queryParamMap.get('token');
    const token: string = this.storage.retrieve('token');

    return token || qs;
  }

  /**
   * Intercept request.
   *
   * @template T - The type of the request.
   * @param {HttpRequest} req Request.
   * @param {HttpHandler} next Next.
   * @returns {Observable<HttpEvent<T>>}
   * @memberof GraphQLRequestInterceptor
   */
  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    return of(true).pipe(
      switchMap(() => {
        if (req.url.includes('/graphql')) {
          const headers = { 'X-Platform-ID': environment.PLATFORM_ID };
          const token = this.token;
          const authorization =
            req.headers.get('authorization') ??
            req.headers.get('Authorization') ??
            req.headers.get('AUTHORIZATION');

          // Only inject token if it's available and Authorization header is not already set
          if (token) {
            if (isEmpty(authorization)) {
              Object.assign(headers, { Authorization: `Bearer ${token}` });
            } else {
              this.logger.warn(
                'Authorization header already set, skipping token injection.'
              );
            }
          }

          const clone = req.clone({ setHeaders: headers });
          return next.handle(clone);
        }
        return next.handle(req);
      })
    );
  }
}

/**
 * GraphQL Response Interceptor.
 *
 * @exports
 * @class GraphQLResponseInterceptor
 */
@Injectable()
export class GraphQLResponseInterceptor implements HttpInterceptor {
  /**
   * Intercept response.
   *
   * @template T - The type of the request.
   * @param {HttpRequest} req Request.
   * @param {HttpHandler} next Next.
   * @returns {Observable<HttpEvent<T>>}
   * @memberof GraphQLResponseInterceptor
   */
  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    return next.handle(req).pipe(
      switchMap((event: HttpEvent<T>) => {
        if (event instanceof HttpResponse && req.url.includes('/graphql')) {
          const body = event.body;
          const headers = event.headers;
          const contentType = headers.get('Content-Type') ?? '';
          const encrypted = contentType.toLowerCase().includes('base64');
          if (encrypted) {
            if (
              body !== null &&
              typeof body === 'object' &&
              'data' in body &&
              !isEmpty(body.data)
            ) {
              const data = body.data;
              return from(decrypt(data)).pipe(
                map((decrypted) => {
                  const json = JSON.parse(decrypted);
                  return event.clone({ body: { ...body, data: json } });
                })
              );
            }
          }
        }
        return of(event);
      })
    );
  }
}
